// ════════════════════════════════════════════════════════════════════════
// PAGE — Inviter à un comité (mai 2026)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// Refacto 2026-05-21 : factorisation du panneau résultat dans le module
// partagé `invitation-proposal.ts` (commun à #meeting/:id).
// ════════════════════════════════════════════════════════════════════════

import {
  fetchInvitationProposal,
  renderProposalPanel,
  type InvitationResult,
} from './invitation-proposal.js';

interface ProgramSummary {
  id: number;
  code?: string | null;
  name: string;
  projects?: Array<{ id: number }>;
}

interface ProjectSummary {
  id: number;
  code?: string | null;
  name: string;
}

interface CommitteeSummary {
  id: number;
  code: string;
  name: string;
}

interface StructureSummary {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

interface SavedState {
  programId?: string;
  projectId?: string;
  structureId?: string;
  committeeId?: string;
  withRisks?: boolean;
}

pages.inviteCommittee = async function () {
  setHeader(t('pageInviteCommittee.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  let programs: ProgramSummary[] = [];
  let projects: ProjectSummary[] = [];
  let committees: CommitteeSummary[] = [];
  let structures: StructureSummary[] = [];
  try {
    let allStructures: StructureSummary[];
    [programs, projects, committees, allStructures] = (await Promise.all([
      api.getPrograms(),
      api.getProjects(),
      api.getCommittees(),
      (api.getStructuresIfAllowed() as Promise<StructureSummary[]>).catch(() => []),
    ])) as [ProgramSummary[], ProjectSummary[], CommitteeSummary[], StructureSummary[]];
    structures = (allStructures || [])
      .filter((s) => s.is_active !== false)
      .sort((a, b) => a.code.localeCompare(b.code));
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }
  if (!committees.length) {
    setContent(`<div class="empty-state" style="padding:40px;text-align:center">
      <h3>${esc(t('pageInviteCommittee.emptyTitle'))}</h3>
      <p>${esc(t('pageInviteCommittee.emptyHelp'))}</p>
    </div>`);
    return;
  }

  let saved: SavedState = {};
  try {
    const raw = tlsGet('invite-committee-state');
    if (raw) saved = (JSON.parse(raw) as SavedState) || {};
  } catch {
    saved = {};
  }
  const state: {
    programId: string;
    projectId: string;
    structureId: string;
    committeeId: string;
    withRisks: boolean;
    result: InvitationResult | null;
  } = {
    programId: saved.programId || '',
    projectId: saved.projectId || '',
    structureId: saved.structureId || '',
    committeeId: saved.committeeId || String(committees[0]?.id ?? ''),
    withRisks: !!saved.withRisks,
    result: null,
  };
  function persist(): void {
    try {
      tlsSet(
        'invite-committee-state',
        JSON.stringify({
          programId: state.programId,
          projectId: state.projectId,
          structureId: state.structureId,
          committeeId: state.committeeId,
          withRisks: state.withRisks,
        }),
      );
    } catch {
      /* noop */
    }
  }

  function visibleProjects(): ProjectSummary[] {
    if (!state.programId) return projects;
    const pgId = parseInt(state.programId);
    const prog = programs.find((p) => p.id === pgId);
    if (!prog) return projects;
    const ids = new Set((prog.projects || []).map((p) => p.id));
    return projects.filter((p) => ids.has(p.id));
  }

  function render(): void {
    const progOpts =
      `<option value="">${esc(t('common.none'))}</option>` +
      programs
        .map(
          (p) =>
            `<option value="${p.id}" ${state.programId === String(p.id) ? 'selected' : ''}>${esc(p.code || '')} — ${esc(p.name)}</option>`,
        )
        .join('');
    const projOpts =
      `<option value="">${esc(state.programId ? t('pageInviteCommittee.optAllProgramProjects') : t('pageInviteCommittee.optChooseProject'))}</option>` +
      visibleProjects()
        .map(
          (p) =>
            `<option value="${p.id}" ${state.projectId === String(p.id) ? 'selected' : ''}>${esc(p.code || '')} — ${esc(p.name)}</option>`,
        )
        .join('');
    const structOpts =
      `<option value="">${esc(t('pageInviteCommittee.optNoStructure'))}</option>` +
      structures
        .map(
          (s) =>
            `<option value="${s.id}" ${state.structureId === String(s.id) ? 'selected' : ''}>${esc(s.code)} — ${esc(s.label)}</option>`,
        )
        .join('');
    const commOpts = committees
      .slice()
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
      .map(
        (c) =>
          `<option value="${c.id}" ${state.committeeId === String(c.id) ? 'selected' : ''}>${esc(c.code)} — ${esc(c.name)}</option>`,
      )
      .join('');

    setContent(`
      <div class="card" style="max-width:900px">
        <h2 style="font-size:16px;margin-bottom:14px">${esc(t('pageInviteCommittee.scopeTitle'))}</h2>
        <div style="display:flex;flex-direction:column;gap:10px">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <span style="min-width:120px;color:var(--text3)">${esc(t('vocabulary.program'))}</span>
            <select id="ic-program" style="flex:1;padding:5px 8px;border:1px solid var(--border2);border-radius:var(--radius)">${progOpts}</select>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <span style="min-width:120px;color:var(--text3)">${esc(t('vocabulary.project'))}</span>
            <select id="ic-project" style="flex:1;padding:5px 8px;border:1px solid var(--border2);border-radius:var(--radius)">${projOpts}</select>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <span style="min-width:120px;color:var(--text3)">${esc(t('vocabulary.structure'))}</span>
            <select id="ic-structure" style="flex:1;padding:5px 8px;border:1px solid var(--border2);border-radius:var(--radius)">${structOpts}</select>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <span style="min-width:120px;color:var(--text3)">${esc(t('vocabulary.committee'))}</span>
            <select id="ic-committee" style="flex:1;padding:5px 8px;border:1px solid var(--border2);border-radius:var(--radius)">${commOpts}</select>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
            <input type="checkbox" id="ic-with-risks" ${state.withRisks ? 'checked' : ''}>
            <span>${esc(t('pageInviteCommittee.includeRisks'))}</span>
          </label>
        </div>
        <div style="margin-top:16px;display:flex;gap:8px">
          <button id="ic-go" class="btn btn-primary">${esc(t('pageInviteCommittee.btnPropose'))}</button>
        </div>
      </div>
      <div id="ic-result" style="margin-top:16px"></div>
    `);

    document.getElementById('ic-program')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.programId = target?.value || '';
      state.projectId = '';
      persist();
      render();
    });
    document.getElementById('ic-project')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.projectId = target?.value || '';
      persist();
    });
    document.getElementById('ic-structure')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.structureId = target?.value || '';
      persist();
    });
    document.getElementById('ic-committee')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.committeeId = target?.value || '';
      persist();
    });
    document.getElementById('ic-with-risks')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      state.withRisks = !!target?.checked;
      persist();
    });
    document.getElementById('ic-go')?.addEventListener('click', () => {
      void onCompute();
    });

    if (state.result) {
      const host = document.getElementById('ic-result');
      if (host) renderProposalPanel(host, state.result);
    }
  }

  async function onCompute(): Promise<void> {
    if (!state.programId && !state.projectId && !state.structureId) {
      toast(t('pageInviteCommittee.errPickProgramOrProject'), 'error');
      return;
    }
    if (!state.committeeId) {
      toast(t('pageInviteCommittee.errPickCommittee'), 'error');
      return;
    }
    try {
      state.result = await fetchInvitationProposal({
        programId: state.programId ? parseInt(state.programId) : null,
        projectId: state.projectId ? parseInt(state.projectId) : null,
        structureId: state.structureId ? parseInt(state.structureId) : null,
        committeeId: parseInt(state.committeeId),
        withRisks: state.withRisks,
      });
      const host = document.getElementById('ic-result');
      if (host) renderProposalPanel(host, state.result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast(t('pageInviteCommittee.errPrefix') + ' ' + msg, 'error');
    }
  }

  render();
};

export {};
